import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';
import { defineMessage, FormattedMessage } from 'react-intl';
import {
  Outlet,
  useLocation,
  useNavigation,
  useRouteLoaderData,
  useSearchParams,
  type MiddlewareFunction,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { shouldRevalidateIgnoringBookingDialogParams } from '~/routes/checkout/booking/booking-dialog/revalidation';
import {
  DIALOG_OWNED_PARAMS,
  isDialogPath,
  stripDialogSuffix,
} from '~/routes/checkout/booking/booking-dialog/upcoming-events-params';
import { useTrackOnce } from '~/domain/plugins/use-track-once';
import { storefrontContext } from '~/domain/core/index.server';
import { loggerContext } from '~/lib/logger';
import { notFound } from '~/lib/react-router';
import { buildPageSeo } from '~/lib/seo';
import { localeHref } from '~/lib/use-locale-href';
import { createServerIntl } from '~/lib/localization/server-intl.server';
import type { RouteHandle } from '~/lib/header-config';
import type { RootLoaderData } from '~/root';
import { PageCard } from '~/ui/app-shell/layouts';
import { Hero } from '~/ui/hero';
import { HostCard } from '~/ui/host-card';
import { renderHostDescription } from '~/ui/host-card/host-description.server';
import { CrossSellSection } from '~/ui/cross-sell-section';
import { ArrowUpRightIcon } from '~/ui/icons/arrow-up-right';
import type { Route } from './+types/route';
import { parseUpcomingEventsFilters } from './page.server';
import { loadUpcomingEventsPage } from './loader.server';
import { EventGrid } from './event-grid';
import { FilterControls } from './filter-controls';
import { UpcomingEventsEmptyState } from './empty-state';
import { legacyParamsMiddleware } from './legacy-params.server';
import { activeFilterKeys, trackPageViewed } from './tracking';

export const handle = {
  // Parity with the legacy page's `backHref="/"`.
  header: { backLink: '/' },
} satisfies RouteHandle;

// Redirects the legacy comma-separated filter params to the canonical repeated
// keys before the loader reads them.
export const middleware: MiddlewareFunction[] = [legacyParamsMiddleware];

/**
 * The booking dialog is a child route that keeps its state in search params,
 * so opening it, closing it, or changing anything inside it is a navigation
 * with a changed search string — which by default would re-run this page's
 * 250-event loader for data it already has.
 *
 * Two things follow from skipping that:
 *
 * - the dialog opens straight away, streaming its own sections behind
 *   skeletons, instead of waiting on a 250-event fetch it doesn't use;
 * - the list behind the dialog stops flashing back to its loading state every
 *   time the visitor steps a date or changes tickets.
 *
 * Closing is the exception: the visitor is looking at the list again, so it
 * refreshes once on the way out and picks up any availability that moved while
 * they were deciding.
 */
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  const isClosingDialog =
    isDialogPath(args.currentUrl.pathname) &&
    !isDialogPath(args.nextUrl.pathname);
  if (isClosingDialog) return true;

  return shouldRevalidateIgnoringBookingDialogParams(args, DIALOG_OWNED_PARAMS);
}

/**
 * Doubles as the `<h1>` and the SEO page title, so the two can't drift.
 * `defineMessage` rather than an inline object: the formatjs build plugin only
 * injects an `id` into `defineMessage` calls, and `formatMessage` throws at
 * runtime without one.
 */
const TITLE = defineMessage({
  defaultMessage: 'Upcoming events',
  description:
    "Heading of the upcoming events page, which lists every scheduled event across the storefront's experiences",
});

export async function loader({ context, url, params }: Route.LoaderArgs) {
  const storefront = context.get(storefrontContext);
  const logger = context.get(loggerContext);

  // The legacy page gated on `features.showUpcomingEventsInStorefront.enabled`
  // and returned `notFound`. Same gate, same outcome: operators who haven't
  // turned the list on shouldn't have a URL that renders an empty page.
  if (!storefront.preferences.showUpcoming) throw notFound();

  const page = await loadUpcomingEventsPage({
    storefront,
    // Mirror the home loader: downstream fetchers key their caches on the
    // resolved locale so they align with the click-through routes.
    locale: storefront.resolvedLocale,
    params,
    currentUrl: url,
    filters: parseUpcomingEventsFilters(url),
    logger,
  });

  // `meta` runs without React context, so the title is resolved here and
  // carried in loader data (the pattern `routes/terms` uses).
  const intl = createServerIntl(context);
  return {
    ...page,
    title: intl.formatMessage(TITLE),
    // Page chrome below the list, sourced the same way the home page sources
    // it. Deliberately outside `loadUpcomingEventsPage`, which short-circuits
    // on a storefront with nothing listed — these blocks are about the
    // company, not the feed, so they render either way.
    crossSell: {
      giftCards: storefront.preferences.giftCards,
      punchcards: storefront.preferences.punchcards,
    },
    company: {
      // Still read by the HostCard of a tab loaded before the description was
      // rendered here, when this loader revalidates under it. Drop it once
      // that build is gone.
      description: storefront.companyProfile.description ?? null,
      // The booking dialog (`…/book`) can open without re-running this
      // loader, so links resolve against the page path both share.
      hostDescription: renderHostDescription(
        storefront.companyProfile.description,
        stripDialogSuffix(url.pathname)
      ),
      coverImage: storefront.companyProfile.coverImage ?? null,
    },
  };
}

export function meta({ loaderData, matches }: Route.MetaArgs) {
  const root = matches.find((m) => m?.id === 'root')?.loaderData as
    RootLoaderData | undefined;
  if (!root) return [];

  return buildPageSeo({
    storefront: {
      fqdn: root.fqdn,
      languages: root.languages,
      defaultLanguage: root.defaultLanguage,
      resolvedLocale: root.locale,
      companyName: root.companyName,
      preview: root.preview,
      seoEnabled: root.preferences.seo,
    },
    // The bare path, deliberately without the filter params: including them
    // would fragment the canonical across every filter permutation, and
    // robots.txt / sitemap.txt already publish the bare path.
    buildPath: (locale) => localeHref(locale, '/:locale?/upcoming-events'),
    pageTitle: loaderData?.title,
  });
}

export default function UpcomingEvents({ loaderData }: Route.ComponentProps) {
  const {
    events,
    title,
    showFromPrice,
    locations,
    languages,
    tags,
    experiences,
    from,
    crossSell,
    company,
  } = loaderData;

  // Company name and email live on the root loader, as on the home page —
  // they're storefront-wide, not this page's to fetch.
  const rootLoaderData = useRouteLoaderData<RootLoaderData>('root');

  // The feed is server-filtered, so a filter change is a navigation. Dim the
  // list while it's in flight so clicking a filter gives instant feedback
  // (mirrors the home page's treatment of its events carousel).
  //
  // Scoped to navigations that actually reload the list, NOT to
  // `navigation.state` alone: every dialog interaction is a navigation too, so
  // keying off the state dimmed the list behind the open dialog on every date
  // step — even though `shouldRevalidate` had already skipped the loader.
  // Leaving for another page doesn't dim either: those cards are about to
  // unmount, and flashing them first just looks like a fault.
  const navigation = useNavigation();
  const location = useLocation();
  const pendingPathname = navigation.location?.pathname;
  const pending =
    pendingPathname !== undefined &&
    !isDialogPath(pendingPathname) &&
    stripDialogSuffix(pendingPathname) === stripDialogSuffix(location.pathname);

  // Reports which filters the view was narrowed by, as the legacy page did.
  // `useTrackOnce` waits for the analytics plugins to finish initialising, and
  // re-fires when the filter selection changes — matching legacy, whose effect
  // was keyed on its filter object.
  const [searchParams] = useSearchParams();
  const filterSignature = activeFilterKeys(searchParams).join(',');
  // Keyed on the signature, not `searchParams`: that's a fresh object every
  // render, which would re-fire this on unrelated navigations.
  useTrackOnce(() => {
    trackPageViewed(searchParams);
  }, [filterSignature]);

  return (
    <PageCard>
      {/* Same rhythm as the home page: the heading, its filters and the list
          sit close together, and the sections below them are set apart. */}
      <Stack gap="5xlarge">
        <Stack gap={{ base: 'large', tablet: 'xlarge' }}>
          <Hero title={title} />

          <FilterControls
            locations={locations}
            languages={languages}
            tags={tags}
            experiences={experiences}
            from={from}
          />

          <div
            aria-busy={pending || undefined}
            className={cx(pending && pendingStyles)}
          >
            {events.length === 0 ? (
              <UpcomingEventsEmptyState />
            ) : (
              <EventGrid events={events} showFromPrice={showFromPrice} />
            )}
          </div>
        </Stack>

        <CrossSellSection
          giftCards={crossSell.giftCards}
          punchcards={crossSell.punchcards}
        />

        <HostCard
          header={rootLoaderData?.companyName}
          description={company.hostDescription}
          coverPhoto={company.coverImage}
        >
          {rootLoaderData?.companyName && (
            <LinkButton
              href={`mailto:${rootLoaderData?.companyEmail}`}
              appearance="secondary"
              size="large"
            >
              <FormattedMessage
                defaultMessage="Contact us"
                description="Action on the host card on the home page that opens a mailto link"
              />
              <ArrowUpRightIcon size="small" />
            </LinkButton>
          )}
        </HostCard>
      </Stack>

      {/* The booking dialog, when `…/book` is matched. */}
      <Outlet />
    </PageCard>
  );
}

const pendingStyles = css({
  opacity: '[0.5]',
  transition: '[opacity 150ms ease-out]',
  pointerEvents: 'none',
});
